'use client';
import styles from './index.module.scss';

import { useDetectOutsideClick } from 'components/hooks/useDetectOutsideClick';
import NotificationSvg from 'components/svg/NotificationSvg';
import CreateButton from 'components/ui-new/Button';
import IconButton from 'components/ui-new/IconButton';
import Notifications from 'components/ui/Notifications';
import classnames from 'classnames';
import Modes from 'components/ui/Modes';
import { useIndexPageContext } from 'context/index_page_state';
import { useAppContext } from 'context/state';
import { useUserContext } from 'context/user_state';
import { ItemsNotificationType } from 'data/interfaces/INotification';
import { ProfileType } from 'data/interfaces/IRegistration';
import NotificationRepository from 'data/repositories/NotificationRepository';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ModalType,
  MonetizationTariff,
  ProductType,
  Routes,
  SalesBanners,
  SnackbarType,
} from 'types/enums';

import Placements from 'components/layout/Header/Placements';
import StarSvg from 'components/svg/FavoriteSvg';
import DropDown from 'components/ui-new/DropDown';
import LanguageSelector from 'components/ui/LanguageSelector';
import Button from 'components/ui/Button';
import { useTariffContext } from 'context/tariff_state';
import { OrderStatus } from 'data/interfaces/Common';
import { breakpoints } from 'styles/variables';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { NAVBAR_EVENT_NAME } from '../NavBar/helpers';
import { NavBarEvent } from '../NavBar/types';
import Maia from './Maia';
import Profile from './Profile';
import navigateBetweenRouters from 'shared/lib/navigateBetweenRouters';

interface Props {
  static?: boolean;
  authorized?: boolean;
  topBannerRef?: MutableRefObject<HTMLAnchorElement>;
}

const Header: FC<Props> = (props: Props) => {
  const t = useTranslations();
  const userContext = useUserContext();
  const appContext = useAppContext();
  const indexPageContext = useIndexPageContext();
  const { orders } = useTariffContext();

  const { isTabletWidth } = appContext.size;
  const { setProfileType } = userContext;
  const [isOpenNavBar, setIsOpenNavBar] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dropDown, openDropDown] = useDetectOutsideClick(dropRef, false);
  const refreshNotificationInterval = useRef<ReturnType<typeof setInterval>>(null);
  const router = useRouter();
  const activePath = router.asPath;

  const isInfluencerCvPage = activePath.includes('influence_hub');

  const headerHeight = 86;
  const headerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const prevScrollY = useRef(typeof window === 'object' ? window.scrollY : 0);
  const offsetRef = useRef(0);

  const handleGoAuth = () => {
    /**
     * Специальная функция перехода используется, потому что модуль авторизации написан на app роутере
     */
    navigateBetweenRouters(Routes.Auth);
  };

  const hirerStatsTypes = [
    ItemsNotificationType.NewApplication,
    ItemsNotificationType.Admin,
    ItemsNotificationType.ChatMessage,
  ];

  const employeeStatsTypes = [
    ItemsNotificationType.NewProposal,
    ItemsNotificationType.ChatMessage,
    ItemsNotificationType.Admin,
  ];
  const [_stats, setStats] = useState<{ type: ItemsNotificationType; count: number }[]>([]);
  const statsRef = useRef<{ type: ItemsNotificationType; count: number }[]>([]);

  const navBarHandle: EventListener = useCallback((e) => {
    const details = (e as NavBarEvent).detail;
    setIsOpenNavBar(details.isVisible);
  }, []);

  const calculateScrollHeight = (addOffset: number) => {
    const currentScrollY = Math.max(window.scrollY, 0);
    const delta = currentScrollY - prevScrollY.current;
    let newOffset = offsetRef.current - delta;
    newOffset = Math.min(0, Math.max(newOffset, -1 * (headerHeight + addOffset)));

    // Calculate opacity based on scroll position (0-200px) only for influencer_cv page
    if (isInfluencerCvPage) {
      const maxScrollForOpacity = 200;
      const calculatedOpacity = Math.min(currentScrollY / maxScrollForOpacity, 1);
      setOpacity(calculatedOpacity);
    } else {
      setOpacity(0); // No transparency for other pages
    }

    return { currentScrollY, newOffset };
  };

  const handleScroll = useCallback(() => {
    const { currentScrollY, newOffset } = calculateScrollHeight(60);

    offsetRef.current = newOffset;
    setOffset(newOffset);
    prevScrollY.current = currentScrollY;
    if (props.topBannerRef?.current == null) return;
    props.topBannerRef.current.style.transform = `translateY(${newOffset}px)`;
  }, [props.topBannerRef]);

  useEffect(() => {
    const emitterNode = window.document.body;
    emitterNode.addEventListener(NAVBAR_EVENT_NAME, navBarHandle);

    return () => {
      emitterNode.removeEventListener(NAVBAR_EVENT_NAME, navBarHandle);
    };
  }, []);

  const handleClickScrollTablet = useCallback(() => {
    if (window.innerWidth > breakpoints.TabletWidth) return;

    const { newOffset, currentScrollY } = calculateScrollHeight(0);

    offsetRef.current = newOffset;
    prevScrollY.current = currentScrollY;
    if (props.topBannerRef?.current == null) return;
    props.topBannerRef.current.style.transform = `translateY(${newOffset}px)`;
  }, [props.topBannerRef]);

  useEffect(() => {
    if (props.static) return;

    prevScrollY.current = Math.max(window.scrollY, 0);
    window.addEventListener('scroll', handleClickScrollTablet, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isTabletWidth) {
      document.body.style.overflow = dropDown ? 'hidden' : 'unset';
    }
  }, [dropDown]);

  useEffect(() => {
    if (!userContext.currentProfile) {
      clearInterval(refreshNotificationInterval.current!);
      refreshNotificationInterval.current = null;
      setStats([]);
    }
    if (!refreshNotificationInterval.current && userContext.currentProfile) {
      const allowedStatsTypes =
        (userContext.currentProfile.profileType === ProfileType.Hirer && hirerStatsTypes) ||
        (userContext.currentProfile.profileType === ProfileType.Employee && employeeStatsTypes);
      setStats(allowedStatsTypes.map((t) => ({ type: t, count: 0 })));
      NotificationRepository.getUnreadStats()
        .then((res) => {
          indexPageContext.setUnreadStats(res.items);
          setStats(
            allowedStatsTypes.map((t) => ({
              type: t,
              count: res.items.find((i) => i.type === t)?.count || 0,
            })),
          );
          statsRef.current = allowedStatsTypes.map((t) => ({
            type: t,
            count: res.items.find((i) => i.type === t)?.count || 0,
          }));
        })
        .catch((err) => console.warn(err));

      refreshNotificationInterval.current = setInterval(() => {
        NotificationRepository.getUnreadStats()
          .then((res) => {
            indexPageContext.setUnreadStats(res.items);
            res.items.forEach((el, index) => {
              const resCount = statsRef.current.find((i) => i.type === el.type)?.count || 0;
              if (el.count > resCount && resCount) {
                const message = `${t('notifications_youhave')} ${
                  el.type === ItemsNotificationType.NewProposal ||
                  el.type === ItemsNotificationType.NewApplication
                    ? t('notifications_newResponse')
                    : t('notifications_newMessage')
                }`;
                if (index === 0) {
                  appContext.showSnackbar(message, SnackbarType.success, 4000);
                } else {
                  setTimeout(() => {
                    appContext.showSnackbar(message, SnackbarType.success, 4000);
                  }, 4100 * index);
                }
              }
            });
            setStats(
              allowedStatsTypes.map((t) => ({
                type: t,
                count: res.items.find((i) => i.type === t)?.count || 0,
              })),
            );
            statsRef.current = allowedStatsTypes.map((t) => ({
              type: t,
              count: res.items.find((i) => i.type === t)?.count || 0,
            }));
          })
          .catch((err) => console.warn(err));
      }, 20000);
    }
  }, [userContext.currentProfile]);

  const { countStandard, countPremium } = useMemo(() => {
    let countStandard = 0;
    let countPremium = 0;
    orders
      .filter(
        (o) =>
          o.product.contents.type === ProductType.VACANCY &&
          (o.status === OrderStatus.PAID || o.status === OrderStatus.USE),
      )
      .forEach((order) => {
        if (order.contents.tariff === MonetizationTariff.Premium)
          countPremium += order.contents.currentCount;
        if (
          [MonetizationTariff.Standard, MonetizationTariff.Optimum].includes(order.contents.tariff)
        )
          countStandard += order.contents.currentCount;
      });
    return { countStandard, countPremium };
  }, [orders]);

  const isShowBanner =
    appContext?.banners &&
    appContext?.banners?.find(
      (el) => el.position.toUpperCase() === SalesBanners.Top.toUpperCase(),
    ) &&
    userContext.isLogged;

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapper__withTopBanner]: isShowBanner,
      })}>
      <div
        ref={headerRef}
        className={classnames(styles.root, {
          [styles.root__open]: isOpenNavBar,
          [styles.static]: props.static,
          [styles.noProfile]: !userContext.currentProfile || !props.authorized,
          [styles.root__transparent]: router.pathname === '/personal/my',
          [styles['root-influencer-cv']]: isInfluencerCvPage,
          [styles.root__withTopBanner]: isShowBanner,
          [styles['root-influencer-cv']]: isInfluencerCvPage,
        })}
        id='header-root'
        style={{
          transform: `translateY(${offset}px)`,
          ...(isInfluencerCvPage
            ? {
                backgroundColor:
                  opacity > 0 ? `rgba(var(--baseBackground-rgb), ${opacity * 0.8})` : 'transparent',
                backdropFilter: `blur(${opacity * 8}px)`,
              }
            : {}),
        }}>
        <div
          className={classnames(
            styles.contentWrapper,
            props.authorized && styles.contentWrapper_authorized,
            {
              contentWrapper: !userContext.isLogged,
              [styles.contentWrapper__withTopBanner]: isShowBanner,
            },
          )}>
          {(!userContext.isLogged || !userContext.currentProfile) && (
            <Modes
              buttons={[
                { name: t('employee'), type: ProfileType.Employee },
                { name: t('hirer'), type: ProfileType.Hirer },
              ]}
              activeButton={userContext.profileType}
              onSet={(type) => setProfileType(type)}
            />
          )}

          {/* {!isTabletWidth && userContext.isLogged && userContext.currentProfile?.profileType && (
            <div className={styles.linksWrapper}>
              <IconButton href='/recruiting' icon={<SearchSvg size='medium' />} />
            </div>
          )} */}

          {!isTabletWidth && userContext.isLogged && userContext.currentProfile?.profileType && (
            <div className='flex' />
          )}

          {!isTabletWidth ? (
            <div
              className={classnames(styles.userWrapper, {
                [styles.userWrapper_unlogged]: !userContext.currentProfile,
              })}>
              {!userContext.currentProfile && (
                <>
                  <Button
                    styleType={['black', 'borderBlue', 'backTransparent']}
                    altStyleType={['white', 'borderGrey']}
                    title='registration'
                    onClick={handleGoAuth}>
                    {t('registration')}
                  </Button>

                  <Button
                    title='login'
                    styleType={['grey', 'borderWhite', 'whiteText', 'backTransparent']}
                    altStyleType={['gradient']}
                    onClick={handleGoAuth}>
                    {t('login')}
                  </Button>
                </>
              )}
              {userContext.isLogged &&
                userContext.currentProfile?.profileType === ProfileType.Employee && (
                  <>
                    <CreateButton variant='grey' href='/create/cv'>
                      {t('create_cv')}
                    </CreateButton>
                    <DropDown
                      isOpen={dropDown}
                      className={styles.dropdown}
                      ref={dropRef}
                      placement='bottomRight'
                      dropdownItem={
                        <Notifications
                          active={dropDown}
                          stats={_stats}
                          onClose={() => openDropDown(false)}
                        />
                      }>
                      <IconButton
                        buttonRef={headerRef}
                        onClick={() => openDropDown(!dropDown)}
                        icon={
                          (_stats?.map((el) => el.count).filter(Boolean).length > 0 && (
                            <NotificationSvg isActive={true} />
                          )) || <NotificationSvg />
                        }
                      />
                    </DropDown>
                    <IconButton
                      href='/favorites'
                      icon={
                        <Image
                          src='/icons/heart-sans-serif.svg'
                          width={24}
                          height={24}
                          alt='heart-sans-serif'
                        />
                      }
                    />
                    <Maia />
                    <LanguageSelector />
                    <Profile />
                  </>
                )}

              {userContext.isLogged &&
                userContext.currentProfile?.profileType === ProfileType.Hirer && (
                  <>
                    <CreateButton variant='grey' href='/create/vacancy'>
                      {t('create_vacancy')}
                    </CreateButton>
                    <DropDown
                      isOpen={dropDown}
                      className={styles.dropdown}
                      ref={dropRef}
                      placement='bottomRight'
                      dropdownItem={
                        <Notifications
                          active={dropDown}
                          stats={_stats}
                          onClose={() => openDropDown(false)}
                        />
                      }>
                      <IconButton
                        buttonRef={headerRef}
                        onClick={() => openDropDown(!dropDown)}
                        icon={
                          (_stats?.map((el) => el.count).filter(Boolean).length > 0 && (
                            <NotificationSvg isActive={true} />
                          )) || <NotificationSvg />
                        }
                      />
                    </DropDown>
                    <IconButton href='/favorites' icon={<StarSvg />} />
                    <Maia />
                    <Placements
                      countStandard={countStandard}
                      countPremium={countPremium}
                      href={Routes.Wallet}
                    />
                    <LanguageSelector />
                    <Profile />
                  </>
                )}
            </div>
          ) : (
            <>
              {props.authorized && (
                <div className={styles.notifications}>
                  <IconButton
                    buttonRef={headerRef}
                    onClick={() => openDropDown(!dropDown)}
                    icon={
                      (_stats?.map((el) => el.count).filter(Boolean).length > 0 && (
                        <NotificationSvg isActive={true} />
                      )) || <NotificationSvg />
                    }
                  />
                  <Maia />
                  {dropDown && (
                    <Notifications
                      active={dropDown}
                      stats={_stats}
                      onClose={() => {
                        openDropDown(false);
                      }}
                    />
                  )}
                </div>
              )}
              <Button
                onClick={() => {
                  appContext.showModal(ModalType.MobileSideBarModal);
                }}
                styleType={['icon']}>
                <Image src='/icons/burger.svg' alt='burger' width={24} height={24} />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
